import { buildStationTicket, buildTestSlip } from './stationTicket';
import { forgetPrinterTrouble, humaniseError, printRaw } from './qzPrinting';

/**
 * Sending the tickets.
 *
 * The rules here are the phone's, and each one exists because of something
 * that goes wrong in a kitchen:
 *
 *   • Nothing is retried. A second attempt at a printer that is switched off
 *     wastes time nobody has mid-service, and a second attempt at one that
 *     was merely slow prints the order twice.
 *
 *   • A failure is named. The waiter has to know WHICH station to walk a
 *     handwritten ticket to, so the station's name comes back, not a count.
 *
 *   • A printing problem is never an order problem. The order is saved before
 *     any of this runs, so nothing here can make a saved order look failed.
 */

/**
 * @returns {Promise<{ printed: string[], failed: {name: string, message: string}[] }>}
 */
export async function printStationTickets({ order, plan, waiterName }) {
  const printed = [];
  const failed = [];

  if (!Array.isArray(plan) || !plan.length) return { printed, failed };

  // Every station at once: they are different machines, and the queue inside
  // qzPrinting keeps two tickets for the SAME machine in order.
  await Promise.all(
    plan.map(async (entry) => {
      const target = { host: entry.printer.host, port: entry.printer.port };
      try {
        await printRaw(target, buildStationTicket({ order, entry, waiterName }));
        printed.push(entry.printer.name);
      } catch (error) {
        failed.push({
          name: entry.printer.name,
          message: humaniseError(error, target),
        });
      }
    })
  );

  return { printed, failed };
}

/** The slip that proves which machine an address belongs to. */
export async function printTestSlip(printer) {
  // A test is somebody standing at the printer asking "is it working NOW".
  // Answering that from the memory of a failure a moment ago would be worse
  // than useless, so this one always goes and looks.
  forgetPrinterTrouble();

  const target = { host: printer.host, port: printer.port };
  try {
    await printRaw(target, buildTestSlip(printer));
    return { ok: true };
  } catch (error) {
    return { ok: false, message: humaniseError(error, target) };
  }
}

/**
 * One sentence a cashier can act on, or nothing at all when every ticket
 * printed. Kept out of the components so the wording is the same wherever a
 * ticket is sent from.
 */
export function describeFailures(failed) {
  if (!failed || !failed.length) return '';

  if (failed.length === 1) {
    return `The ${failed[0].name} ticket did not print. ${failed[0].message}`;
  }

  const names = failed.map((entry) => entry.name).join(', ');
  return `These tickets did not print: ${names}. Write them by hand.`;
}
